import React, { useState } from 'react';
import { t } from 'i18n';
import {
  Row,
  Col,
  Input,
  styled,
  Button,
  Modal,
  Select,
} from '@teko/ds-desktop';
import { BlockPromotion } from '@teko/ui-cart';
import { CartPromotionGroup } from '@teko/cart/v2';
import { getTrackingData } from '@teko/tracking';
import { useTenantConfig } from 'hooks';
import { ApplyVoucherParams } from 'types';
import { logger } from '@teko/log';

interface ApplyPromotionParams {
  productId?: string;
  promotionCode?: string;
  promotionId: number;
}

interface OrderPromotionsModalProps {
  onApplyVoucher: (applyVoucherParams: ApplyVoucherParams) => Promise<unknown>;
  promotionGroups: CartPromotionGroup[];
  onApplyPromotion: (
    applyPromotionParams: ApplyPromotionParams,
  ) => Promise<unknown>;
  onRemovePromotion: (cartPromotionId: string) => Promise<unknown>;
  visible: boolean;
  onClose: () => void;
}

export const OrderPromotionsModal = ({
  onApplyVoucher,
  onApplyPromotion,
  onRemovePromotion,
  promotionGroups,
  visible,
  onClose,
}: OrderPromotionsModalProps) => {
  const { orderPromotionSource } = useTenantConfig();
  const showPromotionSource = !!orderPromotionSource?.length;
  const [inputVoucher, setInputVoucher] = useState('');
  const [promotionSource, setPromotionSource] = useState(
    showPromotionSource ? orderPromotionSource[0].value : '',
  );

  const applyVoucher = async (code: string) => {
    if (!code.length) return;
    try {
      await onApplyVoucher({
        ppmCode: code,
        promotionSource,
      });
    } catch (e) {
      logger.error(e);
    } finally {
      setInputVoucher('');
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      onClose={onClose}
      cancelText={t('Đóng')}
      okText=""
      title={t('Khuyến mãi và mã giảm giá')}
      showCloseIcon={false}
      width="600px"
    >
      <Container>
        <StyledRow justify="space-between">
          {showPromotionSource && (
            <Col span={3.5}>
              <Select
                marginRight="s12"
                width="fit-content"
                allowDelete={false}
                value={promotionSource}
                options={orderPromotionSource}
                selectValue={(value) => setPromotionSource(value.toString())}
              />
            </Col>
          )}
          <Col span={showPromotionSource ? 6 : 9}>
            <StyledInput
              id="input-coupon"
              value={inputVoucher}
              onChange={(value) => {
                setInputVoucher(value);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') await applyVoucher(inputVoucher.trim());
              }}
              placeholder={t('Mã giảm giá/phiếu mua hàng')}
            />
          </Col>
          <Col span={showPromotionSource ? 2.5 : 3}>
            <StyledButton
              width="100%"
              id="apply-coupon-btn"
              disabled={!inputVoucher.trim().length}
              onClick={() => applyVoucher(inputVoucher.trim())}
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'orderPromotionModal',
                contentName: 'applyCouponButton',
              })}
            >
              {t('Áp dụng')}
            </StyledButton>
          </Col>
        </StyledRow>
        {promotionGroups.map((promotionGroup, index) => (
          <StyledBlockPromotion
            key={index}
            title={promotionGroup.title}
            promotions={promotionGroup.promotions}
            onApplyPromotion={onApplyPromotion}
            onRemovePromotion={onRemovePromotion}
            backgroundColor="white"
            styledTitle={{ color: 'black' }}
            showMore={promotionGroups.length > 1 ? 2 : undefined}
            styledPromotionItem={{ marginTop: 's4', marginBottom: 's4' }}
          />
        ))}
      </Container>
    </Modal>
  );
};

const StyledRow = styled(Row)`
  background-color: white;
`;

const StyledInput = styled(Input)`
  flex: 1 1 auto;
  margin: 0 0.75rem 0 0 !important;
`;

const StyledButton = styled(Button)`
  padding: 0 20px;
  min-width: 80px;
`;

const StyledBlockPromotion = styled(BlockPromotion)`
  background-color: white;
  padding: 0.75rem 0;
  border-radius: 0;
`;

const Container = styled.div`
  overflow-y: auto;
  height: 400px;
  margin-top: 12px;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;
